import { nextTick } from 'vue'

import { toUiFieldPath } from '@/lib/ai/fieldPath'
import { resolveFocusFieldAfterImageUpload } from '@/lib/ai/nextFieldPrompt'
import { awaitingFieldFromSession, genaisisPathToMaklerFormKey } from '@/lib/ai/onboardingChat'
import bus, { eventNames } from '@/lib/eventBus'
import COMPANY from '@/modules/company/queries/Company.gql'
import UPDATE_ONBOARDING_IMAGES from '@/modules/company/queries/UpdateOnboardingCompanyImages.gql'
import UPDATE_COMPANY_AMBASSADOR_PORTRAIT from '@/modules/settings/companySettings/queries/UpdateCompanyAmbassadorPortrait.gql'
import { useAiAssistantStore } from '@/stores/aiAssistant'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type CompanyData = Record<string, any>
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ApolloOp = (options: any) => Promise<any>

export type OnboardingImageKind = 'logo' | 'team' | 'profile'

function firstNonEmptyUrl(...candidates: unknown[]): string {
  for (const candidate of candidates) {
    const value = typeof candidate === 'string' ? candidate.trim() : ''
    if (value) return value
  }
  return ''
}

export function imageUrlFromCompany(
  company: CompanyData | null | undefined,
  kind: OnboardingImageKind
): string {
  if (kind === 'logo') return firstNonEmptyUrl(company?.images?.logo?.url)
  if (kind === 'team') return firstNonEmptyUrl(company?.images?.team?.url)
  return firstNonEmptyUrl(company?.ambassador?.image?.url)
}

/** Persist logo/team/portrait and return the CDN URL genaisis needs for image_selection. */
export async function persistOnboardingImageFile(
  deps: {
    company: CompanyData | null
    mutate: ApolloOp
    query?: ApolloOp
    extraRefetchQueries?: unknown[]
  },
  { kind, file }: { kind: OnboardingImageKind; file: File }
): Promise<string | undefined> {
  const companyId = deps.company?.id
  if (!companyId) return undefined
  const refetch = [
    { query: COMPANY, variables: { id: companyId } },
    ...(deps.extraRefetchQueries ?? []),
  ]

  if (kind === 'profile') {
    // Mutation is Void — resolve the portrait URL from a fresh company query, same
    // contract as logo/team taking images.*.url from updateOnboardingCompanyImages.
    await deps.mutate({
      mutation: UPDATE_COMPANY_AMBASSADOR_PORTRAIT,
      variables: {
        input: {
          companyId,
          image: { id: deps.company?.ambassador?.image?.id ?? null, file },
        },
      },
      refetchQueries: refetch,
      awaitRefetchQueries: true,
    })
    if (!deps.query) return undefined
    const { data } = await deps.query({
      query: COMPANY,
      variables: { id: companyId },
      fetchPolicy: 'network-only',
    })
    return firstNonEmptyUrl(data?.company?.ambassador?.image?.url) || undefined
  }

  const { data } = await deps.mutate({
    mutation: UPDATE_ONBOARDING_IMAGES,
    variables: {
      input: {
        companyId,
        images: [
          {
            type: kind,
            image: { id: deps.company?.images?.[kind]?.id ?? null, file },
          },
        ],
      },
    },
    refetchQueries: refetch,
    awaitRefetchQueries: true,
  })
  const images = data?.updateOnboardingCompanyImages?.images
  return firstNonEmptyUrl(kind === 'logo' ? images?.logo?.url : images?.team?.url) || undefined
}

export async function uploadOnboardingImageFromChat(
  deps: {
    company: CompanyData | null
    mutate: ApolloOp
    query?: ApolloOp
    t: (key: string, ...args: unknown[]) => string
    onImageUploaded: (payload: { kind?: OnboardingImageKind; url?: string }) => Promise<void>
  },
  { formKey, file }: { formKey: string; file: File }
): Promise<void> {
  const kindByFormKey: Record<string, OnboardingImageKind> = {
    companyLogo: 'logo',
    teamPicture: 'team',
    portraitImage: 'profile',
  }
  const kind = kindByFormKey[formKey]
  if (!kind || !deps.company?.id) return
  try {
    const uploadedUrl = await persistOnboardingImageFile(
      { company: deps.company, mutate: deps.mutate, query: deps.query },
      { kind, file }
    )
    await deps.onImageUploaded({ kind, url: uploadedUrl })
  } catch (err) {
    console.error('[Onboarding] chat image upload failed', err)
    bus.$emit(eventNames.SHOW_SNACKBAR, {
      color: 'error',
      text: deps.t('alerts.settings.company.error'),
    })
  }
}

export async function handleOnboardingImageUploaded(deps: {
  kind?: OnboardingImageKind
  url?: string
  wizardStep: number
  getCompany: () => CompanyData | null
  refetchCompany: () => Promise<unknown>
  populateFormFromCompany: (c: CompanyData) => void
  applyImageField?: (field: 'companyLogo' | 'teamPicture' | 'portraitImage', url: string) => void
  buildAndSyncAiContext: (imageOverrides?: Record<string, string>) => void
  enterDataContextValues: (o?: Record<string, string>) => Record<string, unknown>
  isFieldRequired?: (field: string) => boolean
}): Promise<void> {
  const fieldByKind = {
    logo: 'companyLogo',
    team: 'teamPicture',
    profile: 'portraitImage',
  } as const
  const fieldKey = deps.kind ? fieldByKind[deps.kind] : undefined
  if (!fieldKey || !deps.kind || deps.wizardStep !== 2) return
  const imageOverrides = deps.url ? { [fieldKey]: deps.url } : {}
  try {
    await deps.refetchCompany()
  } catch (e) {
    console.warn('[Onboarding] company refetch after image upload failed', e)
  }
  const refreshedCompany = deps.getCompany()
  if (refreshedCompany) deps.populateFormFromCompany(refreshedCompany)
  const uploadedUrl = firstNonEmptyUrl(
    deps.url,
    imageUrlFromCompany(refreshedCompany, deps.kind),
    deps.enterDataContextValues(imageOverrides)[fieldKey]
  )
  if (uploadedUrl) {
    deps.applyImageField?.(fieldKey, String(uploadedUrl))
  }
  await nextTick()
  deps.buildAndSyncAiContext(imageOverrides)
  const aiStore = useAiAssistantStore()
  aiStore.markFieldUpdatedByAi(fieldKey)
  aiStore.dismissFieldAiSuggestion(fieldKey)
  const contextValues = deps.enterDataContextValues(imageOverrides)
  let session = aiStore.lastOnboardingSession
  if (uploadedUrl) {
    const url = String(uploadedUrl)
    // Same contract as picking a scraped candidate — writes the CDN URL into
    // the genaisis draft and advances awaiting_field off this image. The
    // `_uploadedImageKey` poll is assistant-mode-gated in bottimmo and does not
    // notify genaisis (logo loop). widgetBusy hides the picker while this runs.
    const applied = await aiStore.applyOnboardingWidgetTool('image_selection', {
      form_key: fieldKey,
      url,
    })
    if (applied) {
      const hasCard = aiStore.completedInlineWidgets.some(
        (widget) =>
          widget.type === 'image' && widget.formKey === fieldKey && widget.imageUrl === url
      )
      if (!hasCard) {
        aiStore.archiveCompletedImageWidget({ formKey: fieldKey, url })
      }
    }
    session = aiStore.lastOnboardingSession
  } else {
    session = await aiStore.pullOnboardingSessionState({ _uploadedImageKey: fieldKey })
  }
  const awaitingFormKey = session
    ? genaisisPathToMaklerFormKey(awaitingFieldFromSession(session))
    : null
  for (const imageField of ['companyLogo', 'teamPicture', 'portraitImage']) {
    aiStore.dismissFieldAiSuggestion(toUiFieldPath(imageField))
  }
  const nextField = resolveFocusFieldAfterImageUpload({
    uiContext: aiStore.uiContext,
    values: contextValues,
    justUploaded: fieldKey,
    awaitingFormKey,
    isFieldRequired: deps.isFieldRequired,
  })
  if (nextField) aiStore.markFieldSuggestedByAi(toUiFieldPath(nextField), { focus: true })
}
